import type { ButtonHTMLAttributes, FC, KeyboardEvent, RefObject } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

import { ChevronDown, Search } from 'lucide-react';

import { Input } from '@shared/components/ui';
import { DismissButton } from '@shared/components/ui/custom/buttons/DismissButton';
import { cn } from '@shared/utils';

import { GenericPopover } from '../GenericPopover';

import { MultiSelectOptionButton } from './MultiSelectOptionButton';
import { useMultiSelectKeyboardNavigation } from './useMultiSelectKeyboardNavigation';

export interface GenericMultiSelectOption {
  value: string;
  label: string;
  count?: number;
  disabled?: boolean;
}

type OmittedButtonKeys = 'onChange' | 'value' | 'defaultValue' | 'children';

export interface GenericMultiSelectProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, OmittedButtonKeys> {
  options: GenericMultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  onClose?: () => void;
}

export const GenericMultiSelect: FC<GenericMultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select values',
  searchPlaceholder = 'Search...',
  disabled = false,
  className,
  onClose,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null) as RefObject<HTMLButtonElement>;
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (isOpen && triggerRef.current) {
      setPopoverWidth(triggerRef.current.offsetWidth);
    }
  }, [isOpen]);

  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null) as RefObject<HTMLInputElement>;

  const showSearch = options.length > 8;

  const filteredOptions = searchQuery
    ? options.filter((opt) => opt.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : options;

  const handleOpenChange = (nextIsOpen: boolean) => {
    setIsOpen(nextIsOpen);
    setFocusedIndex(0);
    if (!nextIsOpen) {
      setSearchQuery('');
      onClose?.();
    }
  };

  function handleToggle(optionValue: string) {
    const isRemoving = value.includes(optionValue);
    const newValue = isRemoving ? value.filter((v) => v !== optionValue) : [...value, optionValue];
    onChange(newValue);
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isOpen && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  const getLabel = (val: string) => options.find((opt) => opt.value === val)?.label ?? val;

  useMultiSelectKeyboardNavigation({
    isOpen,
    showSearch,
    searchInputRef,
    filteredOptions,
    focusedIndex,
    setFocusedIndex,
    setIsOpen,
    handleToggle,
    optionRefs,
    triggerRef,
  });

  const getTriggerLabel = () => {
    if (value.length === 1) return getLabel(value[0] ?? '');
    if (value.length > 1) return `${value.length} selected`;

    return placeholder;
  };

  return (
    <GenericPopover
      open={isOpen}
      onOpenChange={handleOpenChange}
      trigger={
        <div className="relative w-full">
          <button
            {...rest}
            ref={triggerRef}
            type="button"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls="multiselect-listbox"
            disabled={disabled}
            onKeyDown={handleKeyDown}
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30 dark:hover:bg-input/50 cursor-pointer',
              value.length > 0 && 'pr-8',
              className,
            )}
            data-testid="multi-select-trigger"
          >
            <span
              className={cn('flex-1 truncate text-left', value.length === 0 && 'text-muted-foreground')}
              data-testid="multi-select-value-container"
            >
              {getTriggerLabel()}
            </span>
            {value.length === 0 && <ChevronDown className="size-4 shrink-0 opacity-50 ml-2" />}
          </button>

          {value.length > 0 && (
            <DismissButton
              aria-label="Clear all selections"
              disabled={disabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange([]);
                onClose?.();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100"
              data-testid="multi-select-clear-all"
            />
          )}
        </div>
      }
      contentClassName={cn(
        'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 relative z-50 min-w-[8rem] overflow-hidden rounded-md border shadow-md p-0',
      )}
      style={popoverWidth ? { width: popoverWidth } : undefined}
      align="start"
      sideOffset={4}
      onEscapeKeyDown={(e) => e.stopPropagation()}
    >
      {showSearch && (
        <div className="flex items-center border-b border-border px-3 py-2">
          <Search className="size-4 text-muted-foreground mr-2 shrink-0" />
          <Input
            ref={searchInputRef}
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground"
            data-testid="multi-select-search-input"
            aria-label="Search options"
            id="multiselect-search-input"
          />
        </div>
      )}

      <div className="max-h-60 overflow-y-auto scroll-container p-1">
        {filteredOptions.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">No results found</div>
        ) : (
          filteredOptions.map((option, index) => (
            <MultiSelectOptionButton
              key={option.value}
              option={option}
              index={index}
              isSelected={value.includes(option.value)}
              isFocused={index === focusedIndex}
              handleToggle={handleToggle}
              setFocusedIndex={setFocusedIndex}
              optionRef={(el) => {
                optionRefs.current[index] = el;
                return undefined;
              }}
              disabled={option.disabled}
            />
          ))
        )}
      </div>
    </GenericPopover>
  );
};
